import type { StyleSpecification } from "@/types/style";
import { validateStyleSpec } from "./validation";

/**
 * Reads a style.json from a File object.
 */
export async function readStyleFile(
  file: File,
): Promise<{ style: StyleSpecification; errors: string[] }> {
  const text = await file.text();
  return parseStyleJson(text);
}

/**
 * Fetches a style.json from a URL.
 */
export async function fetchStyleUrl(
  url: string,
): Promise<{ style: StyleSpecification; errors: string[] }> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch style: ${response.status} ${response.statusText}`);
  }
  const text = await response.text();
  return parseStyleJson(text);
}

function parseStyleJson(
  text: string,
): { style: StyleSpecification; errors: string[] } {
  const parsed = JSON.parse(text) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("Style must be a JSON object, not " + typeof parsed);
  }
  const style = parsed as StyleSpecification;
  if ((style as Record<string, unknown>).version !== 8) {
    throw new Error("Only MapLibre Style Spec v8 is supported");
  }
  const validationErrors = validateStyleSpec(style);
  return {
    style,
    errors: validationErrors.map((e) => e.message),
  };
}

/**
 * Downloads a style object as a JSON file.
 */
export function downloadStyle(style: unknown, filename: string): void {
  const json = JSON.stringify(style, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
